import { useEffect } from "react";
import {
  ClipboardListIcon,
  RotateCcwIcon,
  SlidersHorizontalIcon,
} from "lucide-react";

import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { SadieSimulatorControls } from "@/components/tools/sadie-simulator/SadieSimulatorControls";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";

import { useSadieSimulator } from "@/hooks/useSadieSimulator";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { sadieSimulator } from "@/lib/sadieSimulator";
import { MobileSadieSimulatorConversation } from "@/mobile/components/MobileSadieSimulatorConversation";

const AGENT_NAME = "Sadie";

export const MobileSadieSimulatorPage = () => {
  /* React Hooks */
  const simulator = useSadieSimulator();
  const successToast = useSuccessToast();

  useSetTitle({ title: "Sadie Simulator" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SADIE_SIMULATOR);
  }, []);

  /* Helpers */
  const isStarted = simulator.entries.length > 0;

  /* Handlers */
  const handleCopy = () => {
    void navigator.clipboard.writeText(
      sadieSimulator.formatTranscript(simulator.entries, AGENT_NAME),
    );
    successToast({ description: "Transcript copied" });
  };

  /* Render */
  const render = () => {
    const { defaults, persona, preset, selectedPersona, status } = simulator;
    if (status === "pending") {
      return (
        <Center className="h-svh">
          <LoadingIndicator variant="large" />
        </Center>
      );
    }
    if (status === "error" || !defaults || !preset || !selectedPersona) {
      return (
        <Center className="h-svh flex-col gap-3 px-6 text-center">
          <ErrorText errorMessage="Could not load the simulator defaults." />
          <Button
            onClick={() => void simulator.refetchDefaults()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        </Center>
      );
    }
    return (
      <Column className="h-svh overflow-hidden bg-white">
        <Row className="shrink-0 items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <Column className="min-w-0 flex-1 gap-0.5 pl-1">
            <Text className="text-base font-semibold tracking-tight text-slate-900">
              {AGENT_NAME}
            </Text>
            <Text className="truncate text-xs text-slate-500">
              {persona?.label ?? selectedPersona}
            </Text>
          </Column>
          <Button
            aria-label="Copy transcript"
            className="h-9 w-9 text-slate-500"
            disabled={!isStarted}
            onClick={handleCopy}
            size="icon"
            variant="ghost"
          >
            <ClipboardListIcon className="h-4 w-4" />
          </Button>
          <Button
            aria-label="Reset conversation"
            className="h-9 w-9 text-slate-500"
            disabled={
              simulator.isTurnPending || simulator.isSearchPending || !isStarted
            }
            onClick={simulator.handleReset}
            size="icon"
            variant="ghost"
          >
            <RotateCcwIcon className="h-4 w-4" />
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button
                aria-label="Setup"
                className="h-9 w-9 text-slate-500"
                size="icon"
                variant="ghost"
              >
                <SlidersHorizontalIcon className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent
              className="flex h-[88svh] flex-col gap-4 overflow-y-auto rounded-t-2xl px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
              side="bottom"
            >
              <SheetHeader className="text-left">
                <SheetTitle>Setup</SheetTitle>
                <SheetDescription>
                  Changes apply to the next turn.
                </SheetDescription>
              </SheetHeader>
              <SadieSimulatorControls
                chunked={simulator.chunked}
                defaultOpenerInstructions={
                  simulator.defaultOpenerInstructions ??
                  defaults.openerInstructions
                }
                disabled={
                  simulator.isTurnPending || simulator.isLoadingDefaults
                }
                instructions={simulator.activeInstructions}
                instructionsDirty={simulator.isInstructionsDirty}
                isFitError={simulator.isFitError}
                isFitPending={simulator.isFitPending}
                isLiveFit={simulator.isLiveFit}
                isOpenerDirty={simulator.isOpenerDirty}
                liveFits={simulator.liveFits}
                matchedOpportunities={defaults.matchedOpportunities}
                model={simulator.activeModel}
                models={defaults.models}
                onChunkedChange={simulator.handleChunkedChange}
                onInstructionsChange={simulator.setInstructions}
                onMatchedOpportunityChange={
                  simulator.handleMatchedOpportunityChange
                }
                onModelChange={simulator.setModel}
                onOpenerInstructionsChange={
                  simulator.handleOpenerInstructionsChange
                }
                onPersonaChange={simulator.handlePersonaChange}
                onPresetChange={simulator.handlePresetChange}
                onResetInstructions={simulator.handleResetInstructions}
                onResetOpenerInstructions={
                  simulator.handleResetOpenerInstructions
                }
                onRetryFit={simulator.handleRetryFit}
                onSessionKindChange={simulator.handleSessionKindChange}
                openerInstructions={
                  simulator.openerInstructions ?? defaults.openerInstructions
                }
                personas={defaults.personas}
                presets={defaults.presets}
                selectedMatchedOpportunity={
                  simulator.selectedMatchedOpportunity ??
                  defaults.selectedMatchedOpportunity
                }
                selectedPersona={selectedPersona}
                selectedPreset={preset.key}
                sessionKind={simulator.sessionKind}
                jobSearchSetup={simulator.jobSearchSetup}
                postPipelineSetup={simulator.postPipelineSetup}
                workerInitiatedSetup={simulator.workerInitiatedSetup}
              />
            </SheetContent>
          </Sheet>
        </Row>
        {simulator.isTurnError ? (
          <Row className="items-center justify-center gap-2 border-b border-slate-200 py-2">
            <ErrorText errorMessage="That turn failed." />
            <Button onClick={simulator.handleRetry} size="sm" variant="outline">
              Retry
            </Button>
          </Row>
        ) : null}
        <MobileSadieSimulatorConversation
          agentName={AGENT_NAME}
          entries={simulator.entries}
          isEnded={simulator.isEnded}
          isFollowUpAvailable={simulator.isFollowUpAvailable}
          isHandoffAvailable={simulator.isHandoffAvailable}
          isHandoffPending={simulator.isHandoffPending}
          isLoopedToSession1={simulator.isLoopedToSession1}
          isPending={simulator.isTurnPending || simulator.isSearchPending}
          isSetupLoading={simulator.isLoadingDefaults}
          onContinueToPulse={simulator.handleContinueToPulse}
          onFollowUp={simulator.handleFollowUp}
          onSend={simulator.handleSend}
          onStart={simulator.handleStart}
          priorEntries={simulator.priorEntries}
          workerTextsFirst={simulator.isWorkerInitiated}
        />
      </Column>
    );
  };

  // The mobile shell has no TooltipProvider of its own. The shared controls
  // use tooltips, so this page carries one.
  return <TooltipProvider>{render()}</TooltipProvider>;
};
